import React from 'react'
import { BrowserWindow, KickstarterFrontEnd } from '..'
import _ from 'lodash'
import SyntaxHighlighter from 'react-syntax-highlighter/light'
import dark from 'react-syntax-highlighter/dist/styles/hljs/dark'
import jsonStringify from '@aitodotai/json-stringify-pretty-compact'
import { stripIndent } from 'common-tags/es'
import Typist from 'react-typist'
import * as styles from './styles'

const createCodeSnippet = (text) => {
  return stripIndent`
    $ curl -X POST \\
      https://funding-success.api.aito.ai/api/v1/_predict \\
      -H 'content-type: application/json' \\
      -d '
      {
        "from": "project_status",
        "where": {
          "category": "technology",
          "duration": {"$numeric": 28},
          "goal": {"$numeric": 60000},
        },
        ${text}
      }'
    `
}

function createResponseSnippet() {
  const response = {
    $p: 0.8669790673789294,
    feature: 'success',
    $why: {
      factors: [
        {
          type: 'baseP',
          value: 0.5824108373535493
        },
        {
          variable: 'goal in [50000.0, 69950.0]',
          value: 0.6284269431801215
        },
        {
          variable: 'category:technology',
          value: 1.5290631512213575
        },
        {
          variable: 'duration in [28.0, 28.0]',
          value: 1.2528286824463164
        }
      ]
    }
  }

  return jsonStringify(response, { maxLength: Infinity, maxNesting: 1 })
}

const backendSyntaxStyle = _.merge({}, _.cloneDeep(dark), {
  hljs: {
    background: 'none',
    overflow: 'hidden',
    overflowX: 'hidden',
    overflowY: 'hidden',
    padding: 0,
    margin: '0.5rem 0',
  }
})

class AnimatedWindowStack extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      backendVisible: false,
      categoryTypingDone: false,
      durationTypingDone: false,
      paramsTypingDone: false,
      queryTypingDone: false,
      typingText: ''
    }
  }

  componentWillUnmount() {
    if (this.durationTypeTimeout) {
      clearTimeout(this.durationTypeTimeout)
    }
    if (this.goalTypeTimeout) {
      clearTimeout(this.goalTypeTimeout)
    }
    if (this.resultTimeout) {
      clearTimeout(this.resultTimeout)
    }
    if (this.showBackendTimeout) {
      clearTimeout(this.showBackendTimeout)
    }
    if (this.resetTypingTimeout) {
      clearTimeout(this.resetTypingTimeout)
    }
    if (this.showResponseTimeout) {
      clearTimeout(this.showResponseTimeout)
    }
    if (this.typeTimeout) {
      clearTimeout(this.typeTimeout)
    }
  }

  type = (text, index) => {
    if (text.length <= index) {
      this.setState({ typingText: text }, () => {
        this.showResponseTimeout = setTimeout(
          () => this.setState({
            queryTypingDone: true
          }, () => this.resetTyping()),
          1000
        )
      })
      return
    }

    this.setState({ typingText: text.substring(0, index + 1) })

    const rand = Math.floor(Math.random() * (100))
    this.typeTimeout = setTimeout(() => { this.type(text, index + 1) }, rand)
  }

  resetTyping = () => {
    this.resetTypingTimeout = setTimeout(() => this.setState({
      typingText: '',
      backendVisible: false,
      categoryTypingDone: false,
      durationTypingDone: false,
      paramsTypingDone: false,
      queryTypingDone: false
    }), 4000)
  }

  onCategoryTyped = () => {
    this.durationTypeTimeout = setTimeout(() => this.setState({ categoryTypingDone: true }), 700)
  }

  onDurationTyped = () => {
    this.goalTypeTimeout = setTimeout(() => this.setState({ durationTypingDone: true }), 700)
  }

  onGoalTyped = () => {
    this.resultTimeout = setTimeout(() => this.setState({ paramsTypingDone: true }), 700)
  }

  onFrontEndAnimationDone = () => {
    this.showBackendTimeout = setTimeout(
      () => this.setState(
        { backendVisible: true },
        () => {
          this.type('"predict": "reaches_goal"', 0)
        }
      ),
      1500
    )
  }

  render() {
    const { state, props } = this
    const {
      backendVisible,
      categoryTypingDone,
      durationTypingDone,
      paramsTypingDone,
      queryTypingDone,
      typingText
    } = state
    const { indicator, kickstarterFrontEndContent } = props
    const categoryInput = !backendVisible ? (
      <Typist avgTypingDelay={40} onTypingDone={this.onCategoryTyped}>
        {kickstarterFrontEndContent.categoryInput}
      </Typist>
    ) : null

    const durationInput = categoryTypingDone ? (
      <Typist avgTypingDelay={40} onTypingDone={this.onDurationTyped}>
        {kickstarterFrontEndContent.durationInput}
      </Typist>
    ) : null

    const goalInput = durationTypingDone ? (
      <Typist avgTypingDelay={40} onTypingDone={this.onGoalTyped}>
        {kickstarterFrontEndContent.goalInput}
      </Typist>
    ) : null

    return (
      <styles.Container>
        <styles.BrowserContainer>
          <styles.BackendWindow visible={backendVisible}>
            <BrowserWindow topBarBgColor="#454545" contentBgColor="#393939" titleColor="#eee" title={indicator.backend}>
              <styles.QueryBox>
                <SyntaxHighlighter language="bash" style={backendSyntaxStyle}>
                  {createCodeSnippet(typingText)}
                </SyntaxHighlighter>
                <styles.ResponseContainer>
                  {queryTypingDone ? (
                    <styles.StyledSyntaxHighlighter language="javascript" style={backendSyntaxStyle}>
                      {createResponseSnippet()}
                    </styles.StyledSyntaxHighlighter>
                  ) : <styles.LoadingIcon />}
                </styles.ResponseContainer>
              </styles.QueryBox>
            </BrowserWindow>
          </styles.BackendWindow>
          <styles.FrontendWindow visible={!backendVisible}>
            <BrowserWindow title={indicator.frontend}>
              <KickstarterFrontEnd
                categoryInput={categoryInput}
                goalInput={goalInput}
                durationInput={durationInput}
                content={kickstarterFrontEndContent}
                paramsTypingDone={paramsTypingDone}
                onFrontEndAnimationDone={this.onFrontEndAnimationDone}
              />
            </BrowserWindow>
          </styles.FrontendWindow>
        </styles.BrowserContainer>
      </styles.Container>
    )
  }
}

export default AnimatedWindowStack
